import React from "react";
import { FeatherAlertCircle, FeatherCalendar, FeatherCheckCircle2 } from "@subframe/core";
import {
  FiledComplianceEntity,
  FilingDeadlineEntity,
  FilingsCalendarEntity,
} from "../../domain/entities/HomeSummaryEntity";

interface FilingsCalendarCardProps {
  filings: FilingsCalendarEntity;
  onSelectFiling: () => void;
}

/**
 * "in 4 days" / "due today" / "35 days overdue".
 *
 * Overdue is phrased as elapsed time rather than a negative number, which is how
 * an accountant actually talks about a missed deadline.
 */
const dueLabel = (days: number | null): string => {
  if (days === null) {
    return "";
  }

  if (days < 0) {
    const overdueBy = Math.abs(days);

    return `${overdueBy} ${overdueBy === 1 ? "day" : "days"} overdue`;
  }

  if (days === 0) {
    return "due today";
  }

  return `in ${days} ${days === 1 ? "day" : "days"}`;
};

const DeadlineRow: React.FC<{
  filing: FilingDeadlineEntity;
  onSelect: () => void;
}> = ({ filing, onSelect }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onSelect();
      }
    }}
    className="flex w-full cursor-pointer flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
  >
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex items-center gap-2 overflow-hidden">
        <FeatherCalendar className="shrink-0 text-body font-body text-neutral-500" />
        <span className="line-clamp-1 text-body-bold font-body-bold text-default-font">
          {filing.formTitle}
        </span>
      </div>
      <span
        className={`shrink-0 text-caption font-caption ${
          filing.isOverdue ? "text-error-700" : "text-subtext-color"
        }`}
      >
        {dueLabel(filing.daysUntilDue)}
      </span>
    </div>

    <div className="flex w-full items-center justify-between gap-2">
      <span className="line-clamp-1 text-caption font-caption text-subtext-color">
        {filing.periodLabel}
      </span>
      <span className="shrink-0 text-caption font-caption text-subtext-color">
        {filing.dueDateLabel}
      </span>
    </div>
  </div>
);

const FiledRow: React.FC<{ filing: FiledComplianceEntity }> = ({ filing }) => (
  <div className="flex w-full items-center justify-between gap-2 rounded-md bg-success-50 px-4 py-3">
    <div className="flex items-center gap-2 overflow-hidden">
      <FeatherCheckCircle2 className="shrink-0 text-body font-body text-success-700" />
      <span className="line-clamp-1 text-body font-body text-default-font">
        {filing.formTitle}
        {filing.periodLabel ? ` ${filing.periodLabel}` : ""}
      </span>
    </div>
    <span className="shrink-0 text-caption font-caption text-subtext-color">
      {filing.filedOnLabel}
    </span>
  </div>
);

/**
 * The right-hand filings rail: what is overdue, what is coming up inside the
 * horizon, and what was recently completed.
 */
export const FilingsCalendarCard: React.FC<FilingsCalendarCardProps> = ({
  filings,
  onSelectFiling,
}) => {
  const hasDeadlines = filings.overdue.length > 0 || filings.upcoming.length > 0;

  return (
    <aside className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
      <div className="flex w-full items-center justify-between gap-2">
        <h2 className="text-heading-3 font-heading-3 text-default-font">
          Filings calendar
        </h2>
        <span className="text-caption font-caption text-subtext-color">
          Next {filings.horizonDays} days
        </span>
      </div>

      {hasDeadlines ? (
        <div className="flex w-full flex-col items-start gap-2">
          {filings.overdue.map((filing) => (
            <DeadlineRow
              key={`overdue-${filing.id}`}
              filing={filing}
              onSelect={onSelectFiling}
            />
          ))}
          {filings.upcoming.map((filing) => (
            <DeadlineRow
              key={`upcoming-${filing.id}`}
              filing={filing}
              onSelect={onSelectFiling}
            />
          ))}
        </div>
      ) : (
        <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-3">
          <FeatherAlertCircle className="text-body font-body text-neutral-400" />
          <span className="text-body font-body text-subtext-color">
            Nothing due in the next {filings.horizonDays} days
          </span>
        </div>
      )}

      {filings.recentlyFiled.length > 0 && (
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-caption-bold font-caption-bold text-subtext-color">
            RECENTLY FILED
          </span>
          {filings.recentlyFiled.map((filing) => (
            <FiledRow key={`filed-${filing.id}`} filing={filing} />
          ))}
        </div>
      )}
    </aside>
  );
};
